import { useRef, useState } from "react";

interface Props {
  onSay: (text: string) => void;
  onAsk: (text: string) => void;
  disabled: boolean;
  placeholder?: string;
}

// Two-button composer. The split mirrors the multiplayer reality of
// the channel: humans chat among themselves with Say (no AI call),
// and anyone can summon the agent with Ask. Ask works empty too —
// "weigh in on the thread as it stands" — so we don't need a third
// button for the ping-the-thread case.
export function Composer({ onSay, onAsk, disabled, placeholder }: Props) {
  const [text, setText] = useState("");
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  function autosize() {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 200) + "px";
  }

  function reset() {
    setText("");
    if (taRef.current) taRef.current.style.height = "auto";
  }

  function say() {
    const t = text.trim();
    if (!t || disabled) return;
    onSay(t);
    reset();
  }

  function ask() {
    if (disabled) return;
    onAsk(text.trim());
    reset();
  }

  const askLabel = text.trim() ? "Ask" : "Ask AI";
  const askTitle = text.trim()
    ? "Post your message and ask the AI to respond (Ctrl/Cmd+Enter)"
    : "Ask the AI to weigh in on the thread as it stands";

  return (
    <div className="composer">
      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          autosize();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            ask();
          } else if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            say();
          }
        }}
        placeholder={
          placeholder ??
          "Message the channel… (Enter to Say, Ctrl/Cmd+Enter to Ask, Shift+Enter for newline)"
        }
        disabled={disabled}
        rows={1}
      />
      <div className="composer-actions">
        <button
          className="btn"
          onClick={say}
          disabled={disabled || !text.trim()}
          title="Post your message to the channel without invoking the AI (Enter)"
        >
          Say
        </button>
        <button
          className="btn btn-primary"
          onClick={ask}
          disabled={disabled}
          title={askTitle}
        >
          {askLabel}
        </button>
      </div>
    </div>
  );
}
